/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'./QueryPostProcessor'
], function (QueryPostProcessor) {

	/**
	 * @description Property display value is used for dashboard rendering, and the corresponding member unique id is
	 * used for filters or other actions. V2 query response has property display values embedded in its parent valueSet.
	 * This class maps olap property display value to '.d' in identifier to make v2 query response algin with
	 * current dashboard implementation so v1 and v2 queries can follow the same logic.
	 *
	 * @example The response identifiers with an olap property:
	 * '1': {
	 * 	'ihId': 1,
	 * 	'd': 'Camping Equipment',<-corresponding member display value
	 * 	'u': '[Sales (analysis)].[Products].[Products].[Product line]->[Products].[991]',<-corresponding member unique id
	 * 	'Product line code': {<-property name
	 * 		'v': 991<-property display value
	 * 	}
	 * }
	 *
	 * After process:
	 * '1': {
	 * 	'ihId': 1,
	 * 	'd': 991,
	 * 	'u': '[Sales (analysis)].[Products].[Products].[Product line]->[Products].[991]',<-corresponding member unique id
	 * }
	 **/
	return class PostProcessOlapProperties extends QueryPostProcessor {

		/**
		 * @Constructor
		 * @param {Object} queryResultData The raw query result data
		 * @param {VisualizationAPI} visualization The {@link VisualizationAPI}
		 */
		constructor({ queryResultData, visualization }) {
			super({ queryResultData });
			this.visualization = visualization;
			this.visDataItems = this.visualization.getSlots().getDataItemList() || [];
			this.edges = this._queryResultData.edges;
		}

		/**
		 * Apply the users overrides
		 * @return {QueryResultData}
		 */
		_processData() {
			// TODO Need to handle list viz post endor r5.
			if (this.edges) {
				// Build properties map from slots data items with property id as the key and the property label as the value.
				const properties = {};
				this.visDataItems.forEach((visDataItem) => {
					const metadataColumn = visDataItem.getMetadataColumn();
					if (metadataColumn && metadataColumn.isProperty()) {
						properties[visDataItem.getId()] =  metadataColumn.getLabel();
					}
				});

				const propertiesIds = Object.keys(properties);
				let propertyIdx = -1;
				if (propertiesIds.length) {
					// Build ihId and property name map from query response if query has any property projected
					let ihIdAndPropertyNameMap = {};
					this.edges.forEach(edge => {
						edge.itemClasses.forEach(itemClass => {
							itemClass.h.forEach(header => {
								propertyIdx = propertiesIds.indexOf(header.di);
								if (propertyIdx !== -1) {
									ihIdAndPropertyNameMap[header.ihId] = properties[propertiesIds[propertyIdx]];
								}
							});
						});
						const ihIdsOfProperties = Object.keys(ihIdAndPropertyNameMap);
						if (ihIdsOfProperties.length) {
							// Replace identifiers .d value: replace member caption with projected property value.
							ihIdsOfProperties.forEach(ihId => {
								//edge.identifiers might not be defined;
								edge.identifiers && Object.values(edge.identifiers).forEach(identifier => {
									if (identifier.ihId === parseInt(ihId)) {
										const propertyName = ihIdAndPropertyNameMap[ihId];
										if (identifier[propertyName]) {
											identifier.d = identifier[propertyName].v;
											delete identifier[propertyName];
										}
									}
								});
							});
							ihIdAndPropertyNameMap = {};
						}
					});
				}
			}

			return this._queryResultData;
		}
	};

});
